import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Toaster } from "../components/ui/toaster";
import {
  ArrowRight,
  BookOpen,
  Check,
  CheckCircle2,
  Crown,
  Heart,
  Sparkles,
  Sprout,
} from "lucide-react";
import { brand } from "../mock";

const nextSteps = [
  {
    icon: "BookOpen",
    title: "Open the Finance Course",
    body: "All 8 biblical finance modules are unlocked — including the Credit Mastery curriculum.",
    cta: "Start learning",
    href: "/finance-course",
  },
  {
    icon: "Sparkles",
    title: "Meet Melanie",
    body: "Min. Tyree's assistant is in the bottom-right corner. Ask her anything about Good Fruit.",
    cta: "Try her now",
    href: "/",
  },
  {
    icon: "Heart",
    title: "Apply for funding",
    body: "If God's calling you to start or grow a faith-based business, take the next step.",
    cta: "See funding paths",
    href: "/funding",
  },
];

const iconMap = { BookOpen, Sparkles, Heart };

const WelcomePage = () => {
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    setShowConfetti(true);
    const t = setTimeout(() => setShowConfetti(false), 4000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="bg-black text-white min-h-screen antialiased">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 md:pt-36 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-[#04181a] to-black" />
        <div
          className="absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 50% 0%, #2dd4bf 0%, transparent 50%), radial-gradient(circle at 20% 80%, #14b8a6 0%, transparent 45%)",
          }}
        />
        {showConfetti && (
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {Array.from({ length: 24 }).map((_, i) => (
              <span
                key={i}
                className="absolute block w-2 h-2 rounded-sm"
                style={{
                  left: `${(i * 137) % 100}%`,
                  top: `-10px`,
                  background: i % 3 === 0 ? "#14b8a6" : i % 3 === 1 ? "#2dd4bf" : "#ffffff",
                  animation: `fall ${2 + (i % 5) * 0.4}s ease-in ${(i % 8) * 0.1}s forwards`,
                  transform: `rotate(${(i * 23) % 360}deg)`,
                }}
              />
            ))}
          </div>
        )}

        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-teal-400 text-black shadow-[0_15px_50px_-10px_rgba(20,184,166,0.7)]">
            <CheckCircle2 className="w-10 h-10" strokeWidth={2.5} />
          </div>
          <div className="mt-6 inline-flex items-center gap-2 bg-teal-500/10 border border-teal-400/30 text-teal-200 px-3 py-1.5 rounded-full text-xs tracking-wide">
            <Crown className="w-3.5 h-3.5" />
            <span>Welcome to Good Fruit</span>
          </div>
          <h1 className="mt-5 text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.05]">
            You&apos;re in.{" "}
            <em className="text-teal-300 font-serif font-normal">
              Welcome home.
            </em>
          </h1>
          <p className="mt-6 text-white/70 text-lg max-w-2xl mx-auto leading-relaxed">
            Your subscription is confirmed. A welcome email is on its way —
            check your inbox in the next few minutes for login instructions and
            your first-week roadmap.
          </p>
          <p className="mt-4 text-teal-300/85 italic font-serif text-base">
            {brand.verse}
          </p>
        </div>
      </section>

      {/* Next steps */}
      <section className="bg-white text-black py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.18em] text-teal-700 font-medium">
            Three places to start
          </div>
          <h2 className="mt-3 text-3xl md:text-5xl font-semibold tracking-tight text-black leading-tight max-w-3xl">
            Pick what fits your{" "}
            <em className="text-teal-700 font-serif font-normal">
              first hour.
            </em>
          </h2>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {nextSteps.map((s, i) => {
              const Icon = iconMap[s.icon];
              const isFeatured = i === 0;
              return (
                <Link
                  key={s.title}
                  to={s.href}
                  className={`group rounded-2xl p-7 border transition-all duration-300 hover:-translate-y-1 ${
                    isFeatured
                      ? "bg-black text-white border-black hover:shadow-[0_25px_60px_-25px_rgba(20,184,166,0.5)]"
                      : "bg-white text-black border-black/10 hover:border-teal-600/40 hover:shadow-[0_20px_50px_-25px_rgba(0,0,0,0.2)]"
                  }`}
                >
                  <div
                    className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                      isFeatured
                        ? "bg-teal-400 text-black"
                        : "bg-teal-500/10 text-teal-700 border border-teal-600/20"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <h3 className="mt-5 text-xl font-semibold tracking-tight">
                    {s.title}
                  </h3>
                  <p
                    className={`mt-3 text-sm leading-relaxed ${
                      isFeatured ? "text-white/65" : "text-black/60"
                    }`}
                  >
                    {s.body}
                  </p>
                  <div
                    className={`mt-5 inline-flex items-center gap-1.5 font-semibold text-sm ${
                      isFeatured ? "text-teal-300" : "text-teal-700"
                    }`}
                  >
                    {s.cta}
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* What to expect */}
      <section className="bg-[#04181a] text-white py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="text-xs uppercase tracking-[0.18em] text-teal-400 font-medium">
            What happens next
          </div>
          <h2 className="mt-3 text-3xl md:text-5xl font-semibold tracking-tight text-white leading-tight">
            The first 30 days,{" "}
            <em className="text-teal-300 font-serif font-normal">
              honest expectations.
            </em>
          </h2>

          <ul className="mt-10 space-y-4">
            {[
              "Within an hour: a welcome email from Min. Tyree with login instructions.",
              "Day 1–7: We'll send a short “getting started” email each day with one practical step from the Finance Course.",
              "Day 14: Your first invitation to a live Q&A with the founder (Premium members).",
              "Day 30: A short feedback survey — we'll listen, we'll fix, and we'll write you back personally.",
              "Cancel anytime through the link in any receipt email — no friction, no guilt.",
            ].map((line) => (
              <li
                key={line}
                className="flex items-start gap-3 text-white/85 leading-relaxed"
              >
                <span className="mt-1 w-5 h-5 rounded-full bg-teal-400 text-black flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3" strokeWidth={3.5} />
                </span>
                {line}
              </li>
            ))}
          </ul>

          <div className="mt-12 flex flex-col sm:flex-row gap-3">
            <Link to="/finance-course">
              <Button className="bg-teal-400 hover:bg-teal-300 text-black font-semibold h-12 px-6">
                <Sprout className="w-4 h-4 mr-2" />
                Start the course
              </Button>
            </Link>
            <Link to="/about">
              <Button
                variant="outline"
                className="bg-transparent border border-white/20 text-white hover:bg-white/5 hover:text-teal-300 h-12 px-6"
              >
                Meet the founder
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <Toaster />

      <style>{`
        @keyframes fall {
          0% { transform: translateY(0) rotate(0); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default WelcomePage;
